import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  FileVideo,
  Image,
  Trash2,
  Plus,
  Download,
  Calendar,
  FileText,
  ChevronDown,
  ChevronUp,
  Clock,
  Loader2,
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import type { ChangeEvent } from 'react';
import { useContent, useJobStatuses } from '../../hooks/useApi';
import { api } from '../../services/api';
import { ContentStatusBadge } from '../common/StatusBadge';
import { ProcessingPipeline } from './ProcessingPipeline';
import { DraftCard } from '../drafts/DraftCard';
import { CreateDraftModal } from '../drafts/CreateDraftModal';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorAlert } from '../common/ErrorAlert';
import { formatDistanceToNow, format } from 'date-fns';
import type { ContentClip, Draft, TranscriptSegment, JobType } from '../../types';
import { PLATFORM_INFO } from '../../types';

export function ContentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: content, loading, error, refetch } = useContent(id);
  const [showCreateDraft, setShowCreateDraft] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);
  const [savingTranscript, setSavingTranscript] = useState(false);
  const [approvingTranscript, setApprovingTranscript] = useState(false);
  const [transcriptError, setTranscriptError] = useState<string | null>(null);

  // Always fetch jobs for video content so the pipeline is always visible
  const isVideoContent = content?.mediaType === 'video';
  const isTerminal = content?.status === 'ready' || content?.status === 'error';
  const {
    data: jobs,
    loading: jobsLoading,
    refetch: refetchJobs,
  } = useJobStatuses(id, !!content && !!isVideoContent, !isTerminal);

  // Show pipeline for video content even before jobs are created
  const showPipeline = isVideoContent && content?.status !== 'ready';

  // Track completed job count — refetch content whenever a new job completes
  const completedCount = jobs?.filter(
    (j) => j.status === 'completed' || j.status === 'failed',
  ).length ?? 0;
  const prevCompletedCount = useRef<number>(0);

  useEffect(() => {
    // Whenever a job newly completes, refetch content to pick up
    // transcript, thumbnails, denoised video, drafts, updated status, etc.
    // Skip if content is already in a terminal state (ready/error).
    if (isTerminal) return;
    if (completedCount > prevCompletedCount.current && prevCompletedCount.current >= 0 && jobs && jobs.length > 0) {
      refetch();
    }
    prevCompletedCount.current = completedCount;
  }, [completedCount, jobs, refetch, isTerminal]);

  // Poll content every 12s during active processing to pick up clip updates
  // Never poll when content is in a terminal state (ready/error).
  const isProcessing = !isTerminal && (content?.status === 'processing' || content?.status === 'clips_extracted' || content?.status === 'transcribed');
  const hasActiveJobs = !isTerminal && jobs?.some(
    (j) => j.status === 'queued' || j.status === 'running' || j.status === 'pending',
  );
  useEffect(() => {
    if (isTerminal || (!isProcessing && !hasActiveJobs)) return;
    const interval = setInterval(() => refetch(), 12_000);
    return () => clearInterval(interval);
  }, [isTerminal, isProcessing, hasActiveJobs, refetch]);

  const handleDelete = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await api.deleteContent(id);
      navigate('/library');
    } catch {
      setDeleting(false);
    }
  };

  const handleDraftAction = () => {
    refetch();
  };

  const handleSaveTranscript = async (nextSegments: TranscriptSegment[], nextTranscript: string) => {
    if (!id) return;
    setSavingTranscript(true);
    setTranscriptError(null);
    try {
      await api.updateTranscript(id, { transcript: nextTranscript, segments: nextSegments });
      await refetch();
    } catch (err) {
      setTranscriptError(err instanceof Error ? err.message : 'Failed to save transcript');
      throw err;
    } finally {
      setSavingTranscript(false);
    }
  };

  const handleApproveTranscript = async (nextSegments: TranscriptSegment[], nextTranscript: string) => {
    if (!id) return;
    setApprovingTranscript(true);
    setTranscriptError(null);
    try {
      await api.approveTranscript(id, { transcript: nextTranscript, segments: nextSegments });
      await Promise.all([refetch(), refetchJobs()]);
    } catch (err) {
      setTranscriptError(err instanceof Error ? err.message : 'Failed to approve transcript');
    } finally {
      setApprovingTranscript(false);
    }
  };

  const handleRetryJob = async (jobType: JobType) => {
    if (!id) return;
    await api.retryJob(id, jobType);
    await Promise.all([refetch(), refetchJobs()]);
  };

  if (loading) return <LoadingSpinner message="Loading content..." />;
  if (error) return <ErrorAlert message={error} onRetry={refetch} />;
  if (!content) return <ErrorAlert message="Content not found" />;

  const isVideo = content.mediaType === 'video';
  const transcriptApprovalStatus = String(
    content.metadata?.transcriptApprovalStatus
      || (content.clipStatus === 'captioned_clips_ready' || content.status === 'ready' ? 'approved' : 'pending_approval'),
  );
  const sourceAssetCount = content.sourceAssets?.length ?? content.sourceDownloadUrls?.length ?? 0;
  const isGroupedVideo = isVideo && sourceAssetCount > 1;
  const stitchedReady = isGroupedVideo && !!content.metadata?.stitchedSourceKey && content.sourceUrls?.[0] !== content.sourceUrl;

  // Group drafts by platform
  const draftsByPlatform = (content.drafts || []).reduce<Record<string, Draft[]>>(
    (acc, draft) => {
      if (!acc[draft.platform]) acc[draft.platform] = [];
      acc[draft.platform]!.push(draft);
      return acc;
    },
    {},
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/library"
          className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{content.title}</h1>
          <p className="text-sm text-gray-500">
            Uploaded {formatDistanceToNow(new Date(content.uploadedAt), { addSuffix: true })}
            {' · '}
            {format(new Date(content.uploadedAt), 'MMM d, yyyy h:mm a')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCreateDraft(true)}
            className="btn-primary"
          >
            <Plus className="mr-2 h-4 w-4" />
            Create Draft
          </button>
          {!deleteConfirm ? (
            <button
              onClick={() => setDeleteConfirm(true)}
              className="btn-secondary text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button onClick={handleDelete} disabled={deleting} className="btn-danger">
                {deleting ? 'Deleting...' : 'Confirm Delete'}
              </button>
              <button onClick={() => setDeleteConfirm(false)} className="btn-secondary">
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Processing Pipeline — shown for video content (even before jobs exist) */}
      {(showPipeline || (isVideoContent && jobs && jobs.length > 0)) && (
        <ProcessingPipeline
          jobs={jobs || []}
          loading={jobsLoading}
          clipStatus={content.clipStatus}
          transcriptApprovalStatus={transcriptApprovalStatus}
          onRetryJob={handleRetryJob}
        />
      )}

      {/* Uploaded source clips for grouped uploads */}
      {isGroupedVideo && (
        <SourceClipsSection
          assets={content.sourceAssets || []}
          downloadUrls={content.sourceDownloadUrls || []}
          stitchedReady={stitchedReady}
        />
      )}

      {/* Clips Section — full width, between pipeline and main grid */}
      {isVideo && content.clipsArray && content.clipsArray.length > 0 && (
        <ClipsSection
          clips={content.clipsArray}
          clipStatus={content.clipStatus}
          transcriptApprovalStatus={transcriptApprovalStatus}
        />
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Media Preview */}
        <div className="lg:col-span-1">
          <div className="card overflow-hidden">
            <div className="aspect-video bg-gray-100">
              {content.sourceDownloadUrl && isVideo ? (
                <video
                  src={content.sourceDownloadUrl}
                  controls
                  className="h-full w-full object-contain"
                  poster={content.thumbnailDownloadUrl}
                />
              ) : content.sourceDownloadUrl && !isVideo ? (
                <img
                  src={content.sourceDownloadUrl}
                  alt={content.title}
                  className="h-full w-full object-contain"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  {isVideo ? (
                    <FileVideo className="h-16 w-16 text-gray-300" />
                  ) : (
                    <Image className="h-16 w-16 text-gray-300" />
                  )}
                </div>
              )}
            </div>

            <div className="space-y-3 p-4">
              {isGroupedVideo && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  <div className="font-medium">
                    {stitchedReady ? 'Using stitched video for playback, clips, and downstream publishing.' : 'Stitched video pending...'}
                  </div>
                  <div className="mt-1 text-xs text-amber-700">
                    {stitchedReady
                      ? `Source clips (${sourceAssetCount}) are preserved below for reference.`
                      : `Showing all ${sourceAssetCount} uploaded source clips until stitching finishes.`}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <ContentStatusBadge status={content.status} size="md" />
                <span className={`text-sm font-medium ${isVideo ? 'text-purple-600' : 'text-blue-600'}`}>
                  {isVideo ? 'Video' : 'Image'}
                </span>
              </div>

              {content.description && (
                <p className="text-sm text-gray-600">{content.description}</p>
              )}

              <div className="space-y-1 text-xs text-gray-500">
                {content.duration && (
                  <p>
                    <Calendar className="mr-1 inline h-3 w-3" />
                    Duration: {Math.floor(content.duration / 60)}m {Math.floor(content.duration % 60)}s
                  </p>
                )}
                {typeof content.metadata?.fileSize === 'number' && (
                  <p>Size: {formatFileSize(content.metadata.fileSize)}</p>
                )}
                {content.uploadedByUser?.email && (
                  <p>Uploaded by: {content.uploadedByUser.email}</p>
                )}
              </div>

              {content.sourceDownloadUrl && (
                <a
                  href={content.sourceDownloadUrl}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary w-full"
                >
                  <Download className="mr-2 h-4 w-4" />
                  {stitchedReady ? 'Download Stitched Video' : 'Download Active Source'}
                </a>
              )}
            </div>
          </div>

          {/* Denoised Video */}
          {content.denoisedDownloadUrl && isVideo && (
            <div className="card overflow-hidden mt-4">
              <div className="aspect-video bg-gray-100">
                <video
                  src={content.denoisedDownloadUrl}
                  controls
                  className="h-full w-full object-contain"
                  poster={content.thumbnailDownloadUrl}
                />
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center rounded-full bg-[#004721]/10 px-2.5 py-0.5 text-xs font-medium text-[#004721]">
                    🔇 Denoised
                  </span>
                  <span className="text-xs text-gray-500">Background noise removed</span>
                </div>
                <a
                  href={content.denoisedDownloadUrl}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary w-full mt-3"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download Denoised
                </a>
              </div>
            </div>
          )}

          {/* Transcript Section */}
          {isVideo && (
            <TranscriptSection
              transcript={content.transcript}
              segments={content.transcriptTimestamps}
              expanded={transcriptExpanded}
              onToggle={() => setTranscriptExpanded(!transcriptExpanded)}
              approvalStatus={transcriptApprovalStatus}
              onSave={handleSaveTranscript}
              onApprove={handleApproveTranscript}
              saving={savingTranscript}
              approving={approvingTranscript}
              error={transcriptError}
            />
          )}
        </div>

        {/* Drafts */}
        <div className="lg:col-span-2">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Platform Drafts
                {content.drafts && content.drafts.length > 0 && (
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    ({content.drafts.length})
                  </span>
                )}
              </h2>
            </div>

            {(!content.drafts || content.drafts.length === 0) ? (
              <div className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50/50 p-8 text-center">
                <p className="text-sm text-gray-500">
                  No drafts yet. Create a draft for each platform you want to publish to.
                </p>
                <button
                  onClick={() => setShowCreateDraft(true)}
                  className="btn-primary mt-4"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create First Draft
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(draftsByPlatform).map(([platform, drafts]) => (
                  <div key={platform}>
                    {(drafts as Draft[]).map((draft) => (
                      <DraftCard
                        key={draft.draftId}
                        draft={draft}
                        onAction={handleDraftAction}
                      />
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Draft Modal */}
      {showCreateDraft && (
        <CreateDraftModal
          contentId={content.contentId}
          onClose={() => setShowCreateDraft(false)}
          onCreated={() => {
            setShowCreateDraft(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}

// ── Uploaded Source Clips Section ────────────────────────────────────────

function SourceClipsSection({
  assets,
  downloadUrls,
  stitchedReady,
}: {
  assets: Array<{ s3Key: string; filename: string; trim?: { startSec: number; endSec: number } }>;
  downloadUrls: string[];
  stitchedReady: boolean;
}) {
  return (
    <div className="card overflow-hidden">
      <div className="p-4">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <FileVideo className="h-4 w-4 text-[#004721]" />
              <h3 className="text-sm font-semibold text-gray-900">Uploaded source clips</h3>
              <span className="text-xs text-gray-500">({assets.length})</span>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {stitchedReady
                ? 'These are the original uploads used to build the stitched video now driving the pipeline.'
                : 'These original uploads are visible immediately while the stitched video is still being prepared.'}
            </p>
          </div>
          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${stitchedReady ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
            {stitchedReady ? 'Stitched video ready' : 'Stitched video pending...'}
          </span>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {assets.map((asset, index) => {
            const downloadUrl = downloadUrls[index];
            const trim = asset.trim;
            return (
              <div key={asset.s3Key} className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                <div className="aspect-video bg-black">
                  {downloadUrl ? (
                    <video src={downloadUrl} controls className="h-full w-full object-contain" preload="metadata" />
                  ) : (
                    <div className="flex h-full items-center justify-center">
                      <FileVideo className="h-10 w-10 text-gray-500" />
                    </div>
                  )}
                </div>
                <div className="space-y-2 px-3 py-3">
                  <div>
                    <p className="truncate text-sm font-medium text-gray-900">{asset.filename}</p>
                    <p className="truncate text-[11px] text-gray-400">{asset.s3Key}</p>
                  </div>
                  {trim && (
                    <div className="rounded-md bg-purple-50 px-2 py-1 text-[11px] text-purple-700">
                      Trim: {formatTimestamp(trim.startSec)} → {formatTimestamp(trim.endSec)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Clips Section Component ──────────────────────────────────────────────

function ClipsSection({
  clips,
  clipStatus,
  transcriptApprovalStatus,
}: {
  clips: ContentClip[];
  clipStatus?: string;
  transcriptApprovalStatus?: string;
}) {
  const [expandedClip, setExpandedClip] = useState<string | null>(null);

  return (
    <div className="card overflow-hidden">
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileVideo className="h-4 w-4 text-[#004721]" />
            <h3 className="text-sm font-semibold text-gray-900">Platform Clips</h3>
            <span className="text-xs text-gray-500">({clips.length})</span>
          </div>
          {clipStatus === 'base_clips_ready' && transcriptApprovalStatus !== 'approved' && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700">
              <Clock className="h-3 w-3" />
              Waiting for transcript approval
            </span>
          )}
          {clipStatus === 'captioned_clips_ready' && (
            <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
              ✓ All clips captioned
            </span>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {clips.map((clip) => {
            const platformInfo = PLATFORM_INFO[clip.platform] || {
              name: clip.platform,
              icon: '🎬',
              color: 'bg-gray-500',
            };
            const isExpanded = expandedClip === clip.platform;

            return (
              <div key={clip.platform} className="rounded-lg border border-gray-200 overflow-hidden bg-white">
                {/* Clip header */}
                <button
                  onClick={() => setExpandedClip(isExpanded ? null : clip.platform)}
                  className="flex w-full items-center justify-between px-3 py-2 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-base">{platformInfo.icon}</span>
                    <span className="text-sm font-medium text-gray-900">{platformInfo.name}</span>
                  </div>
                  {clip.status === 'base' ? (
                    <span className="inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                      Base
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700">
                      Captioned
                    </span>
                  )}
                </button>

                {/* Video preview — always show thumbnail, expand for controls */}
                {clip.downloadUrl && (
                  <div className="border-t border-gray-100">
                    <div className={isExpanded ? 'aspect-video bg-black' : 'aspect-video bg-black cursor-pointer'} onClick={() => !isExpanded && setExpandedClip(clip.platform)}>
                      <video
                        src={clip.downloadUrl}
                        controls={isExpanded}
                        preload="metadata"
                        className="h-full w-full object-contain"
                      />
                    </div>
                    {isExpanded && (
                      <div className="flex items-center justify-between px-3 py-2 bg-gray-50">
                        {clip.reason && (
                          <p className="text-xs text-gray-500 flex-1 mr-2 line-clamp-2">{clip.reason}</p>
                        )}
                        <a
                          href={clip.downloadUrl}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-shrink-0 inline-flex items-center gap-1 rounded-md bg-white border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <Download className="h-3 w-3" />
                          Download
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Transcript Section Component ─────────────────────────────────────────

function formatTimestamp(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function TranscriptSection({
  transcript,
  segments,
  expanded,
  onToggle,
  approvalStatus,
  onSave,
  onApprove,
  saving,
  approving,
  error,
}: {
  transcript?: string;
  segments?: TranscriptSegment[];
  expanded: boolean;
  onToggle: () => void;
  approvalStatus: string;
  onSave: (segments: TranscriptSegment[], transcript: string) => Promise<void>;
  onApprove: (segments: TranscriptSegment[], transcript: string) => Promise<void>;
  saving: boolean;
  approving: boolean;
  error: string | null;
}) {
  const hasTranscript = !!transcript || (segments && segments.length > 0);
  const PREVIEW_LENGTH = 200;
  const [draftSegments, setDraftSegments] = useState<TranscriptSegment[]>(segments || []);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDraftSegments(segments || []);
    setDirty(false);
  }, [segments]);

  const fullText = transcript || (segments ? segments.map((s) => s.text).join(' ') : '');
  const currentText = draftSegments.length > 0 ? draftSegments.map((s) => s.text).join(' ') : fullText;
  const isLong = currentText.length > PREVIEW_LENGTH;
  const previewText = isLong ? currentText.slice(0, PREVIEW_LENGTH).trimEnd() + '…' : currentText;
  const canEdit = draftSegments.length > 0;
  const canApprove = hasTranscript && !dirty && approvalStatus !== 'approved';
  const canToggleTranscript = hasTranscript;

  const handleSegmentChange = (index: number, event: ChangeEvent<HTMLTextAreaElement>) => {
    const next = [...draftSegments];
    next[index] = { ...next[index]!, text: event.target.value };
    setDraftSegments(next);
    setDirty(true);
  };

  const handleSave = async () => {
    const cleaned = draftSegments.map((seg) => ({ ...seg, text: seg.text.trim().replace(/\s+/g, ' ') }));
    await onSave(cleaned, cleaned.map((seg) => seg.text).join(' '));
    setDirty(false);
  };

  return (
    <div className="card overflow-hidden">
      <div className="p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-[#004721]" />
            <h3 className="text-sm font-semibold text-gray-900">Transcript</h3>
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${approvalStatus === 'approved' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
              {approvalStatus === 'approved' ? 'Approved' : 'Needs approval'}
            </span>
          </div>
          {canToggleTranscript && (
            <button onClick={onToggle} className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-[#0071B9] hover:bg-blue-50 transition-colors">
              {expanded ? <>Show less<ChevronUp className="h-3 w-3" /></> : <>Show more<ChevronDown className="h-3 w-3" /></>}
            </button>
          )}
        </div>

        {!hasTranscript ? (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-4">
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            <span className="text-sm text-gray-500">Transcript pending…</span>
          </div>
        ) : expanded && canEdit ? (
          <div className="mt-3 space-y-3">
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Edit the transcript line by line. Segment timing stays locked, so corrections keep the timing structure intact.
            </div>
            <div className="max-h-96 space-y-2 overflow-y-auto rounded-lg bg-gray-50 p-3">
              {draftSegments.map((seg, idx) => (
                <div key={idx} className="rounded-lg border border-gray-200 bg-white p-2">
                  <div className="mb-2 flex items-center gap-2 text-xs text-gray-500">
                    <span className="rounded bg-[#004721]/10 px-1.5 py-0.5 font-mono text-[#004721]">{formatTimestamp(seg.start)}</span>
                    <span>→</span>
                    <span className="font-mono">{formatTimestamp(seg.end)}</span>
                  </div>
                  <textarea
                    value={seg.text}
                    onChange={(event) => handleSegmentChange(idx, event)}
                    className="min-h-[72px] w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 outline-none focus:border-[#0071B9] focus:ring-2 focus:ring-blue-100"
                  />
                </div>
              ))}
            </div>
            {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={handleSave} disabled={!dirty || saving} className="btn-primary disabled:cursor-not-allowed disabled:opacity-50">
                {saving ? 'Saving…' : 'Save transcript'}
              </button>
              <button onClick={() => onApprove(draftSegments, currentText)} disabled={!canApprove || approving || saving} className="btn-secondary disabled:cursor-not-allowed disabled:opacity-50">
                {approving ? 'Approving…' : 'Approve transcript & start captions'}
              </button>
              {dirty && <span className="text-xs text-amber-700">Save edits before approval.</span>}
            </div>
          </div>
        ) : expanded ? (
          <div className="mt-3 max-h-96 overflow-y-auto rounded-lg bg-gray-50 p-3">
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">{currentText}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {canEdit && <button onClick={onToggle} className="btn-secondary">Edit transcript</button>}
              <button onClick={() => onApprove(draftSegments, currentText)} disabled={!canApprove || approving || saving} className="btn-primary disabled:cursor-not-allowed disabled:opacity-50">
                {approving ? 'Approving…' : 'Approve transcript & start captions'}
              </button>
            </div>
            {error && <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
          </div>
        ) : (
          <div className="mt-3 rounded-lg bg-gray-50 p-3">
            <p className="text-sm leading-relaxed text-gray-700">{previewText}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
